import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Autocomplete } from "@totalsoft/rocket-ui";

const Autosuggest = ({
  id,
  label,
  value: item,
  simpleValue = true,
  labelKey = "label",
  valueKey = "value",
  delay = 500,
  onChange,
  loadOptions,
  fullWidth = true,
  isSearchable = true,
  isClearable = true,
  disabled,
  error,
  helperText
}) => {
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState(item?.[labelKey] || "");
  const delayRef = useRef();

  const handleInputChange = useCallback(
    (_, newSearch, reason) => {
      if (reason === "reset") return;
      setSearch(newSearch);
      if (delayRef.current) clearTimeout(delayRef.current);
      if (!loadOptions) return;
      if (!newSearch) {
        if (!item) setOptions([]);
        else setOptions([item]);
        return;
      }
      delayRef.current = setTimeout(() => loadOptions(newSearch).then(result => setOptions(result)), [delay]);
    },
    [delay, loadOptions, item]
  );

  useEffect(() => {
    setOptions(item ? [item] : []);
  }, [item]);

  const internalOnChange = useCallback(
    value => {
      const selection = options.find(option => option[valueKey] === value);
      setSearch(selection?.[labelKey]);
      onChange(value, selection?.[labelKey]);
    },
    [onChange, options, labelKey, valueKey]
  );

  return (
    <Autocomplete
      id={id}
      fullWidth={fullWidth}
      isSearchable={isSearchable}
      isClearable={isClearable}
      simpleValue={simpleValue}
      valueKey={valueKey}
      labelKey={labelKey}
      label={label}
      value={item?.value || ""}
      onChange={internalOnChange}
      onInputChange={handleInputChange}
      options={options}
      disabled={disabled}
      error={error}
      helperText={helperText}
      inputValue={search}
    />
  );
};

Autosuggest.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.any,
  simpleValue: PropTypes.bool,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
  delay: PropTypes.number,
  onChange: PropTypes.func,
  loadOptions: PropTypes.func,
  selectedOption: PropTypes.object,
  fullWidth: PropTypes.bool,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string
};

export default Autosuggest;
